import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteTabs } from '@components/ui/RouteTabs';
import { usePermissions } from '@hooks/useAuth';
import { NAV_GROUPS, type NavGroup, type NavGroupId } from './nav-groups';

/**
 * The tab strip a merged sidebar entry lands on (#2476). One strip per group in
 * `nav-groups.ts`, rendered on every page the group's tabs point at.
 *
 * A tab is shown only when the seat holds every permission its route checks, so
 * the strip never offers a page the route guard would bounce. `badges` hangs
 * extra markup after a tab's label, keyed by the tab's route.
 */
export interface NavGroupTabsProps {
  group: NavGroupId;
  badges?: Partial<Record<string, ReactNode>>;
}

export function NavGroupTabs({ group, badges }: NavGroupTabsProps) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const { labelKey, tabs }: NavGroup = NAV_GROUPS[group];

  const visible = tabs.filter((tab) => (tab.permissions ?? []).every((key) => perms.includes(key)));

  return (
    <RouteTabs
      ariaLabel={t(labelKey)}
      tabs={visible.map((tab) => {
        const badge = badges?.[tab.to];
        return {
          to: tab.to,
          label:
            badge != null ? (
              <span className="inline-flex items-center gap-2">
                {t(tab.labelKey)}
                {badge}
              </span>
            ) : (
              t(tab.labelKey)
            ),
        };
      })}
    />
  );
}

export default NavGroupTabs;
